import { useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { CalendarDays, CheckCircle2, Loader2, MapPin, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { submitPublicEvent } from "@/lib/events/submissions.functions";
import { usePublicCities } from "@/hooks/use-public-cities";
import { AutoT } from "@/i18n/AutoT";
import { useAutoTranslate } from "@/i18n/useAutoTranslate";
import { TurnstileWidget, TURNSTILE_SITE_KEY } from "@/components/TurnstileWidget";
import { validateImageFile, compressImage as compressImageFile, MAX_IMAGE_BYTES } from "@/lib/compress-image";

export const Route = createFileRoute("/submit-event")({
  head: () => {
    const url = "https://whatfinland.com/submit-event";
    return {
      meta: [
        { title: "Submit an Event — What Finland" },
        {
          name: "description",
          content:
            "Tell us about your concert, festival, exhibition or club night in Finland. Our editors review every submission before it goes live.",
        },
        { property: "og:title", content: "Submit an Event — What Finland" },
        {
          property: "og:description",
          content: "Share your Finnish event with What. Reviewed by our team before publishing.",
        },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: SubmitEventPage,
  errorComponent: () => (
    <div className="p-10 text-center text-muted-foreground">Couldn't load the form.</div>
  ),
  notFoundComponent: () => <div className="p-10 text-center text-muted-foreground">Not found.</div>,
});

const CATEGORIES = [
  "Concert",
  "Festival",
  "Exhibition",
  "Theatre",
  "Club Night",
  "Sports",
  "Family",
  "Food & Drink",
  "Community",
  "Other",
];

const MAX_IMAGES = 5;
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB per original file
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

/** Client-side contract — mirrors the server schema so errors surface instantly. */
const FormSchema = z
  .object({
    title: z.string().trim().min(3, "Event name must be at least 3 characters.").max(200, "Keep the name under 200 characters."),
    city: z.string().trim().min(2, "Please enter the city.").max(80, "City name is too long."),
    venue: z.string().trim().max(200, "Venue is too long."),
    start_date: z.string().min(1, "Start date and time are required."),
    end_date: z.string(),
    category: z.string(),
    description: z
      .string()
      .trim()
      .min(10, "Add at least 10 characters so people know what to expect.")
      .max(2000, "Description must be under 2000 characters."),
    image_url: z.string().trim().max(600).refine((v) => !v || /^https?:\/\/\S+$/i.test(v), "Enter a valid image URL starting with https://"),
    ticket_url: z.string().trim().max(600).refine((v) => !v || /^https?:\/\/\S+$/i.test(v), "Enter a valid link starting with https://"),
    organizer: z.string().trim().min(2, "Tell us who is submitting this.").max(160, "Name is too long."),
    contact_email: z.string().trim().email("Enter a valid email address.").max(200, "Email is too long."),
  })
  .superRefine((v, ctx) => {
    const start = new Date(v.start_date);
    if (v.start_date && Number.isNaN(start.getTime())) {
      ctx.addIssue({ code: "custom", path: ["start_date"], message: "That start date is not valid." });
    } else if (v.start_date && start.getTime() < Date.now() - 24 * 3600_000) {
      ctx.addIssue({ code: "custom", path: ["start_date"], message: "The start date is in the past." });
    }
    if (v.end_date) {
      const end = new Date(v.end_date);
      if (Number.isNaN(end.getTime())) {
        ctx.addIssue({ code: "custom", path: ["end_date"], message: "That end date is not valid." });
      } else if (!Number.isNaN(start.getTime()) && end < start) {
        ctx.addIssue({ code: "custom", path: ["end_date"], message: "End time cannot be before the start time." });
      }
    }
  });

type Errors = Partial<Record<string, string>>;
type Shot = { id: string; url: string; name: string };

/**
 * Validate (real image bytes + size cap) then downscale/compress in the
 * browser so every attached photo lands under 100 KB.
 */
async function compressImage(input: File): Promise<string> {
  const { file } = await validateImageFile(input, { maxInputBytes: MAX_UPLOAD_BYTES });
  const small = await compressImageFile(file, MAX_IMAGE_BYTES, 1400);
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Could not read image."));
    reader.readAsDataURL(small);
  });
}

function SubmitEventPage() {
  const submit = useServerFn(submitPublicEvent);
  const cities = usePublicCities();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [shots, setShots] = useState<Shot[]>([]);
  const [errors, setErrors] = useState<Errors>({});
  const [captcha, setCaptcha] = useState("");
  const startedAt = useRef(Date.now());
  const [city, setCityValue] = useState("");
  const [cityTouched, setCityTouched] = useState(false);
  const [venue, setVenue] = useState("");
  const [autoCity, setAutoCity] = useState<string | null>(null);
  const [desc, setDesc] = useState("");
  const phCity = useAutoTranslate("Helsinki");
  const chooseCategory = useAutoTranslate("Choose a category");
  const uploadPhoto = useAutoTranslate("Add photos");
  const phDesc = useAutoTranslate("What happens, who is performing, why should people come?");
  const phVenue = useAutoTranslate("e.g. Tavastia, Urho Kekkosen katu 4, Helsinki");

  const cityNames = useMemo(
    () => cities.map((c) => c.name).filter(Boolean).sort((a, b) => a.localeCompare(b)),
    [cities],
  );

  /** Pick the city out of a free-form address / venue string. */
  function detectCity(text: string): string | null {
    const hay = text.toLowerCase();
    let best: string | null = null;
    for (const name of cityNames) {
      const n = name.toLowerCase();
      if (n.length < 3) continue;
      if (new RegExp(`(^|[^\\p{L}])${n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^\\p{L}]|$)`, "iu").test(hay)) {
        if (!best || name.length > best.length) best = name;
      }
    }
    return best;
  }

  function onVenueChange(value: string) {
    setVenue(value);
    const hit = detectCity(value);
    if (hit) {
      setAutoCity(hit);
      if (!cityTouched || !city.trim()) {
        setCityValue(hit);
        setErrors((e) => ({ ...e, city: undefined }));
      }
    } else {
      setAutoCity(null);
    }
  }

  async function onFiles(list: FileList | null) {
    if (!list?.length) return;
    const files = Array.from(list);
    const room = MAX_IMAGES - shots.length;
    if (room <= 0) {
      toast.error(`You can attach up to ${MAX_IMAGES} images.`);
      return;
    }
    const next: Shot[] = [];
    for (const file of files.slice(0, room)) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: only JPG, PNG or WebP images are allowed.`);
        continue;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        toast.error(`${file.name} is larger than 10 MB.`);
        continue;
      }
      try {
        next.push({ id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 7)}`, url: await compressImage(file), name: file.name });
      } catch (err) {
        toast.error(err instanceof Error ? err.message : `Couldn't read ${file.name}.`);
      }
    }
    if (files.length > room) toast.error(`Only the first ${MAX_IMAGES} images are kept.`);
    if (next.length) setShots((s) => [...s, ...next].slice(0, MAX_IMAGES));
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const raw = {
      title: String(fd.get("title") ?? ""),
      city: String(fd.get("city") ?? ""),
      venue: String(fd.get("venue") ?? ""),
      start_date: String(fd.get("start_date") ?? ""),
      end_date: String(fd.get("end_date") ?? ""),
      category: String(fd.get("category") ?? ""),
      description: String(fd.get("description") ?? ""),
      image_url: String(fd.get("image_url") ?? ""),
      ticket_url: String(fd.get("ticket_url") ?? ""),
      organizer: String(fd.get("organizer") ?? ""),
      contact_email: String(fd.get("contact_email") ?? ""),
    };

    const parsed = FormSchema.safeParse(raw);
    if (!parsed.success) {
      const next: Errors = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "");
        if (key && !next[key]) next[key] = issue.message;
      }
      setErrors(next);
      const first = document.querySelector<HTMLElement>(`[data-field="${Object.keys(next)[0]}"]`);
      first?.scrollIntoView({ behavior: "smooth", block: "center" });
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setErrors({});

    const payload = {
      ...parsed.data,
      image_data: shots[0]?.url ?? "",
      images: shots.slice(1).map((s) => s.url),
      is_organizer: fd.get("is_organizer") === "on",
      is_free: fd.get("is_free") === "on",
      signup_required: fd.get("signup_required") === "on",
      hp: String(fd.get("company_website") ?? ""),
      elapsed_ms: Date.now() - startedAt.current,
      captcha_token: captcha,
    };
    if (TURNSTILE_SITE_KEY && !captcha) {
      toast.error("Please complete the anti-spam check.");
      return;
    }
    setBusy(true);
    try {
      await submit({ data: payload });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Submission failed. Please check the form.");
    } finally {
      setBusy(false);
    }
  }


  if (done) {
    return (
      <div className="mx-auto max-w-xl px-5 py-20 sm:py-24 text-center">
        <div className="mx-auto grid size-16 place-items-center rounded-full bg-emerald-500/10">
          <CheckCircle2 className="size-9 text-emerald-500" />
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl mt-5"><AutoT text="Thanks — Your Event Is In Review" /></h1>
        <p className="mt-3 text-sm text-muted-foreground">
          <AutoT text="Our editors check every submission for quality and relevance. Once approved, your event appears automatically on What and in its city calendar." />
        </p>
        <Link
          to="/happening"
          className="mt-8 inline-block rounded-full bg-foreground px-6 py-3 text-xs font-bold uppercase tracking-[0.2em] text-background"
        >
          <AutoT text="Back to Events" />
        </Link>
        <p className="mt-5 text-sm text-muted-foreground">
          <Link to="/my-events" search={{ token: undefined }} className="underline">
            <AutoT text="Track My Submissions" />
          </Link>
        </p>
      </div>
    );
  }

  const inputCls =
    "w-full rounded-xl border border-border/60 bg-background px-3.5 py-3 text-[15px] sm:text-sm outline-none transition-colors focus:border-foreground/40 focus:ring-2 focus:ring-foreground/10";
  const errorCls = "border-destructive/70 focus:border-destructive focus:ring-destructive/20";
  const labelCls = "block text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-1.5";
  const cardCls = "rounded-3xl border border-border/60 bg-card p-4 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]";
  const fieldCls = (name: string) => `${inputCls} ${errors[name] ? errorCls : ""}`;

  function FieldError({ name }: { name: string }) {
    if (!errors[name]) return null;
    return (
      <p role="alert" className="mt-1.5 text-xs font-medium text-destructive">
        <AutoT text={errors[name] as string} />
      </p>
    );
  }
  const clearError = (name: string) => setErrors((e) => (e[name] ? { ...e, [name]: undefined } : e));


  function StepHead({ n, title, hint }: { n: number; title: string; hint?: string }) {
    return (
      <div className="mb-4 flex items-start gap-3">
        <span className="grid size-7 shrink-0 place-items-center rounded-full bg-foreground text-[11px] font-bold text-background">
          {n}
        </span>
        <div className="min-w-0">
          <h2 className="font-heading text-base sm:text-lg leading-tight"><AutoT text={title} /></h2>
          {hint && <p className="mt-0.5 text-xs text-muted-foreground"><AutoT text={hint} /></p>}
        </div>
      </div>
    );
  }

  return (
    <div className="pb-52 sm:pb-16">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60 bg-gradient-to-b from-foreground/[0.06] to-transparent">
        <div className="mx-auto max-w-3xl px-5 py-10 sm:py-14">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-background/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]">
            <CalendarDays className="size-3.5" /> <AutoT text="Events" />
          </span>
          <h1 className="font-heading text-[1.8rem] leading-[1.15] sm:text-4xl mt-4 text-balance">
            <AutoT text="Submit an event" />
          </h1>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-xl">
            <AutoT text="Concerts, festivals, exhibitions, markets — anything happening in Finland. Open to everyone: organizers, businesses, cities, associations and individuals. No account needed." />
          </p>
          <ul className="mt-5 flex flex-wrap gap-2 text-[11px] font-semibold">
            {["Free to submit", "Reviewed by editors", "Live in 1–2 days"].map((chip) => (
              <li key={chip} className="rounded-full border border-border/60 bg-background/70 px-3 py-1.5 text-muted-foreground">
                <AutoT text={chip} />
              </li>
            ))}
          </ul>
        </div>
      </section>

      <form onSubmit={onSubmit} className="mx-auto max-w-3xl px-4 sm:px-5 py-6 sm:py-10 space-y-4 sm:space-y-6">
        {/* 1 — Basics */}
        <section className={cardCls}>
          <StepHead n={1} title="The basics" hint="Name, place and when it happens." />
          <div className="space-y-4">
            <div data-field="title">
              <label className={labelCls} htmlFor="title"><AutoT text="Event name" /> *</label>
              <input id="title" name="title" maxLength={200} className={fieldCls("title")} aria-invalid={!!errors.title} onChange={() => clearError("title")} />
              <FieldError name="title" />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div data-field="venue" className="sm:order-2">
                <label className={labelCls} htmlFor="venue"><AutoT text="Venue / address" /></label>
                <input
                  id="venue"
                  name="venue"
                  value={venue}
                  onChange={(e) => onVenueChange(e.target.value)}
                  placeholder={phVenue}
                  className={fieldCls("venue")}
                />
                <FieldError name="venue" />
              </div>
              <div data-field="city" className="sm:order-1">
                <label className={labelCls} htmlFor="city"><AutoT text="City" /> *</label>
                <input
                  id="city"
                  name="city"
                  list="submit-event-cities"
                  value={city}
                  onChange={(e) => { setCityValue(e.target.value); setCityTouched(true); clearError("city"); }}
                  placeholder={phCity}
                  className={fieldCls("city")}
                  aria-invalid={!!errors.city}
                />
                <datalist id="submit-event-cities">
                  {cityNames.map((n) => <option key={n} value={n} />)}
                </datalist>
                {autoCity && city === autoCity && !errors.city && (
                  <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
                    <MapPin className="size-3" /> <AutoT text="City detected from the address" />
                  </p>
                )}
                <FieldError name="city" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div data-field="start_date">
                <label className={labelCls} htmlFor="start_date"><AutoT text="Starts" /> *</label>
                <input id="start_date" name="start_date" type="datetime-local" className={fieldCls("start_date")} onChange={() => clearError("start_date")} />
                <FieldError name="start_date" />
              </div>
              <div data-field="end_date">
                <label className={labelCls} htmlFor="end_date"><AutoT text="Ends" /></label>
                <input id="end_date" name="end_date" type="datetime-local" className={fieldCls("end_date")} onChange={() => clearError("end_date")} />
                <FieldError name="end_date" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground"><AutoT text="Times are Finnish time (Europe/Helsinki)." /></p>
          </div>
        </section>

        {/* 2 — Details */}
        <section className={cardCls}>
          <StepHead n={2} title="Details" hint="Help people decide whether to come." />
          <div className="space-y-4">
            <div>
              <label className={labelCls} htmlFor="category"><AutoT text="Category" /></label>
              <select id="category" name="category" className={inputCls} defaultValue="">
                <option value="">{chooseCategory}</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>


            <div data-field="description">
              <label className={labelCls} htmlFor="description"><AutoT text="Short description" /> *</label>
              <textarea
                id="description"
                name="description"
                rows={5}
                maxLength={2000}
                value={desc}
                onChange={(e) => { setDesc(e.target.value); clearError("description"); }}
                className={fieldCls("description")}
                aria-invalid={!!errors.description}
                placeholder={phDesc}
              />
              <div className="mt-1 flex items-center justify-between gap-3">
                <FieldError name="description" />
                <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">{desc.trim().length}/2000</span>
              </div>
            </div>

            <div data-field="ticket_url">
              <label className={labelCls} htmlFor="ticket_url"><AutoT text="Website / ticket link" /></label>
              <input id="ticket_url" name="ticket_url" inputMode="url" className={fieldCls("ticket_url")} placeholder="https://" onChange={() => clearError("ticket_url")} />
              <FieldError name="ticket_url" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <label className="flex items-start gap-2.5 rounded-xl border border-border/60 px-3.5 py-3 text-sm">
                <input type="checkbox" name="is_free" className="mt-0.5 size-4" />
                <span><AutoT text="This event is free" /></span>
              </label>
              <label className="flex items-start gap-2.5 rounded-xl border border-border/60 px-3.5 py-3 text-sm">
                <input type="checkbox" name="signup_required" className="mt-0.5 size-4" />
                <span><AutoT text="Signup / registration required" /></span>
              </label>
            </div>
          </div>
        </section>

        {/* 3 — Images */}
        <section className={cardCls}>
          <StepHead n={3} title="Event photos" hint="Up to 5 images — JPG, PNG or WebP, max 10 MB each. The first one becomes the cover." />
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-5">
              {shots.map((s, i) => (
                <div key={s.id} className="relative aspect-square overflow-hidden rounded-2xl border border-border/60 bg-muted/40">
                  <img src={s.url} alt={s.name} className="absolute inset-0 size-full object-cover" />
                  {i === 0 && (
                    <span className="absolute left-1.5 top-1.5 rounded-full bg-foreground/85 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-background">
                      <AutoT text="Cover" />
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setShots((list) => list.filter((x) => x.id !== s.id))}
                    aria-label={`Remove ${s.name}`}
                    className="absolute right-1.5 top-1.5 grid size-7 place-items-center rounded-full bg-background/90 shadow-sm"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
              ))}
              {shots.length < MAX_IMAGES && (
                <label className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1.5 rounded-2xl border border-dashed border-border bg-muted/30 text-[11px] font-semibold text-muted-foreground active:scale-[0.98] transition-transform">
                  <Upload className="size-4" />
                  <span className="px-1 text-center leading-tight">{uploadPhoto}</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="hidden"
                    onChange={(e) => { void onFiles(e.target.files); e.currentTarget.value = ""; }}
                  />
                </label>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              <AutoT text="Images are compressed in your browser before upload." /> {shots.length}/{MAX_IMAGES}
            </p>

            <div data-field="image_url">
              <label className={labelCls} htmlFor="image_url"><AutoT text="…or paste an image URL" /></label>
              <input id="image_url" name="image_url" inputMode="url" className={fieldCls("image_url")} placeholder="https://" onChange={() => clearError("image_url")} />
              <FieldError name="image_url" />
            </div>
          </div>
        </section>

        {/* 4 — Contact */}
        <section className={cardCls}>
          <StepHead n={4} title="Who is submitting" hint="We only use this to contact you about the event." />
          <div className="grid sm:grid-cols-2 gap-4">
            <div data-field="organizer">
              <label className={labelCls} htmlFor="organizer"><AutoT text="Organizer name" /> *</label>
              <input id="organizer" name="organizer" className={fieldCls("organizer")} onChange={() => clearError("organizer")} />
              <FieldError name="organizer" />
            </div>
            <div data-field="contact_email">
              <label className={labelCls} htmlFor="contact_email"><AutoT text="Contact email" /> *</label>
              <input id="contact_email" name="contact_email" type="email" inputMode="email" autoComplete="email" className={fieldCls("contact_email")} onChange={() => clearError("contact_email")} />
              <FieldError name="contact_email" />
            </div>
          </div>

          <label className="mt-4 flex items-start gap-2.5 rounded-xl border border-border/60 px-3.5 py-3 text-sm">
            <input type="checkbox" name="is_organizer" className="mt-0.5 size-4" />
            <span><AutoT text="I organize this event" /></span>
          </label>
        </section>

        {/* Honeypot — hidden from humans, irresistible to bots. */}
        <div aria-hidden className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
          <label htmlFor="company_website">Company website</label>
          <input id="company_website" name="company_website" tabIndex={-1} autoComplete="off" />
        </div>

        <TurnstileWidget onToken={setCaptcha} />

        {/* Desktop submit */}
        <div className="hidden sm:flex items-center gap-4">
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-foreground px-7 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-background disabled:opacity-60"
          >
            {busy && <Loader2 className="size-4 animate-spin" />}
            <AutoT text="Submit For Review" />
          </button>
          <p className="text-[11px] text-muted-foreground">
            <AutoT text="Submissions are never published automatically." />
          </p>
        </div>

        {/* Mobile sticky submit bar.
            iOS Safari repaints `backdrop-filter` on every momentum-scroll frame
            for position:fixed layers, which reads as flickering — use an opaque
            background and pin the bar to its own compositing layer instead. */}
        <div
          className="sm:hidden fixed inset-x-0 z-40 border-t border-border/60 bg-background px-4 pt-3 pb-3 gpu-layer"
          style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 78px)" }}
        >
          <button
            type="submit"
            disabled={busy}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-foreground px-6 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-background disabled:opacity-60"
          >
            {busy && <Loader2 className="size-4 animate-spin" />}
            <AutoT text="Submit For Review" />
          </button>
          <p className="mt-2 text-center text-[10px] text-muted-foreground">
            <AutoT text="Reviewed by our team — never published automatically." />
          </p>
        </div>
      </form>
    </div>
  );
}

